import { ITaskData, task } from "@origin-digital/reporting-client";
import { isNative } from "@origin-digital/platform-helpers";

import { appName } from "src/config";
import { trackingIds } from "src/consts";

export const ACCOUNT_TYPES_CLAIM = "https://originenergy.com.au/accountTypes";

export type StepUpCustomerType = "oz" | "msp" | "resi" | "sme" | "unknown";

/**
 * Collapses the access-token `accountTypes` claim (a list of
 * RESIDENTIAL/SME/MSP/C_AND_I/OZ/UNKNOWN) into a single analytics
 * `customerType` label using a fixed precedence:
 *   1. any OZ          -> "oz"
 *   2. any MSP         -> "msp"
 *   3. SME + RESI mix  -> "resi"
 *   4. all SME         -> "sme"
 *   5. all RESIDENTIAL -> "resi"
 *   6. otherwise       -> "unknown"
 */
export const resolveCustomerType = (
  accountTypes: unknown
): StepUpCustomerType => {
  const types = Array.isArray(accountTypes) ? (accountTypes as string[]) : [];
  if (types.includes("OZ")) return "oz";
  if (types.includes("MSP")) return "msp";
  const hasSme = types.includes("SME");
  const hasResi = types.includes("RESIDENTIAL");
  if (hasSme && hasResi) return "resi";
  if (types.length > 0 && types.every((t) => t === "SME")) return "sme";
  if (types.length > 0 && types.every((t) => t === "RESIDENTIAL"))
    return "resi";
  return "unknown";
};

/**
 * Fires a step-up analytics event for the given `action`, reusing the decoded
 * access-token payload so the `accountTypes` claim costs no extra fetch.
 *
 * `task` exposes no callable `task(action, ...)` overload — the published API
 * is `task.base(formName, data)` with `action` inside `data` (see
 * `utils/tracking.ts`). `formName` is mandatory on `ITaskData`; we pass it both
 * positionally and in the payload to match the existing app pattern. The `user`
 * shape requested by the analytics team uses raw Adobe values that don't match
 * reporting-client's typed `IUserDataAA`, hence the cast.
 */
const trackStepUp = (
  action: "start" | "complete",
  payload: Record<string, unknown>
): void => {
  task.base(trackingIds.stepUp, {
    action,
    formName: trackingIds.stepUp,
    appName,
    isNative: isNative(),
    path: window.location.pathname,
    type: "step-up-authentication",
    user: {
      authenticated: "authenticated",
      userType: "customer",
      customerType: resolveCustomerType(payload[ACCOUNT_TYPES_CLAIM]),
      existingCustomer: "yes",
    },
  } as unknown as ITaskData);
};

/**
 * "start" — the moment the MFA page is shown to the user
 * (`StepUpGuard` `onStepUpStart`).
 */
export const trackStepUpStart = (payload: Record<string, unknown>): void =>
  trackStepUp("start", payload);

/**
 * "complete" — the user entered the OTP correctly and landed back on the
 * profile page (`StepUpGuard` `onStepUpComplete`).
 */
export const trackStepUpComplete = (payload: Record<string, unknown>): void =>
  trackStepUp("complete", payload);
